import { FIXED_PAGES, isFixed, type SitePage } from "./registry";

/**
 * ═══ L'ORDRE DES PAGES DU SITE ═══
 *
 * Le menu, la barre du bas et les tuiles de l'accueil se lisent dans l'ordre que
 * l'organisateur a rangé, et non dans celui du registre.
 *
 * ⚠️ UNE SEULE CLÉ POUR TOUTE LA LISTE, et pas un rang par page — même raison que
 * l'ordre des étapes (`funnel/order.ts`) : deux pages ne peuvent pas se retrouver au
 * même rang, un déplacement est UNE écriture, et l'ordre se lit d'un coup d'œil en base.
 *
 * ⚠️ ELLE NE SE CASCADE PAS. La structure du site est commune à l'événement ; seuls les
 * LIBELLÉS se surchargent par langue et par typologie. Un exposant qui verrait le menu
 * dans un autre ordre qu'un visiteur ne pourrait plus se faire expliquer où cliquer.
 *
 * ⚠️ RIEN NE BOUGE SANS RÉGLAGE. La clé absente vaut l'ordre du registre, et une page
 * absente de la liste enregistrée reprend sa place DÉCLARÉE plutôt que d'aller au bout —
 * c'est ce qui fait qu'une page ajoutée au produit plus tard apparaît où elle a été
 * pensée, et pas en queue de menu.
 *
 * Les textes affichés sont français : c'est un produit français.
 */

export const PAGES_ORDER_KEY = "FO_MEETING_V2_PAGES_ORDER";

/**
 * ⚠️ ACCUEIL ET INSCRIPTION OUVRENT LA LISTE, ET NE SE DÉPLACENT PAS. Ce n'est pas une
 * préférence de mise en page : ce sont les deux entrées que le châssis rend à part —
 * l'accueil est la racine (`/`), l'inscription mène à la PREMIÈRE ÉTAPE SERVIE, une
 * adresse qui se calcule. La barre du bas pose l'accueil en tête quoi qu'il arrive. Les
 * ranger ailleurs ferait mentir le gestionnaire sur ce que le site montre.
 */
const FIXED_PATTERN =
  "L'accueil et l'inscription ouvrent le site : ils ne se déplacent pas.";

/** L'ordre enregistré, sous la forme `accueil,inscription,badge,programme`. */
export function readPageOrder(raw: unknown): string[] {
  if (typeof raw !== "string") return [];
  return raw
    .split(",")
    .map((key) => key.trim())
    .filter(Boolean);
}

export function writePageOrder(pages: ReadonlyArray<SitePage>): string {
  return pages.map((p) => p.key).join(",");
}

/**
 * Les pages dans l'ordre rangé : celles qu'on connaît, complétées à leur place déclarée.
 *
 * `toutes` est la liste DÉCLARÉE, dans l'ordre du produit — le registre pour les pages
 * du site, et le parcours pour les étapes qu'on sert aussi comme pages.
 */
export function orderedPages(
  raw: unknown,
  all: ReadonlyArray<SitePage>,
): SitePage[] {
  const byKey = new Map(all.map((p) => [p.key, p]));
  const saved = readPageOrder(raw)
    .flatMap((key) => {
      const page = byKey.get(key);
      return page ? [page] : [];
    })
    /* Les fixes reprennent la tête quoi qu'ait été enregistré : un ordre ancien, écrit
       avant qu'elles ne le soient, ne doit pas les laisser au milieu. */
    .filter((p) => !isFixed(p.key));

  const fixes = all.filter((p) => isFixed(p.key));
  if (saved.length === 0) return [...all];

  const result = [...saved];
  const free = all.filter((p) => !isFixed(p.key));
  free.forEach((missing, i) => {
    if (result.some((p) => p.key === missing.key)) return;
    /* Elle s'insère AVANT la première page qui la suit dans l'ordre déclaré : c'est ce
       qui lui rend sa place plutôt que de la reléguer au bout. */
    const next = new Set(free.slice(i + 1).map((p) => p.key));
    const at = result.findIndex((p) => next.has(p.key));
    result.splice(at < 0 ? result.length : at, 0, missing);
  });

  return [...fixes, ...result];
}

/**
 * Déplacer une page d'un cran, ou dire pourquoi c'est refusé.
 *
 * ⚠️ LE REFUS SE DIT. Une flèche qui ne fait rien sans un mot se lit comme une panne —
 * c'est la leçon du gestionnaire des étapes, et elle vaut ici mot pour mot.
 */
export function movePage(
  pages: ReadonlyArray<SitePage>,
  index: number,
  direction: -1 | 1,
):
  | { pages: SitePage[]; refusal?: undefined }
  | { pages?: undefined; refusal: string } {
  const target = index + direction;
  const page = pages[index];
  const neighbour = pages[target];
  if (!page || !neighbour)
    return { refusal: "Cette page est déjà au bout de la liste." };
  if (isFixed(page.key) || isFixed(neighbour.key)) return { refusal: FIXED_PATTERN };

  const next = [...pages];
  next.splice(index, 1);
  next.splice(target, 0, page);
  return { pages: next };
}

/** Une page se déplace-t-elle ? Le gestionnaire s'en sert pour éteindre ses flèches. */
export const movable = (page: SitePage) => !isFixed(page.key);

/** Le nombre d'entrées que le produit pose en tête et qui ne bougent pas. */
export const HEAD = FIXED_PAGES.length;
